import { SeededRandom } from "./random";

export const WORLD_SCHEMA_VERSION = 1;
export const WORLD_WIDTH = 96;
export const WORLD_HEIGHT = 64;
export const MIN_DEEP_WATER_GAP = 5;
export const MIN_CONTINENT_TILES = 1_200;
export const MAX_CONTINENT_TILES = 2_800;
export const STARTING_POPULATION_PER_CONTINENT = 24;
export const TICKS_PER_SECOND = 10;
export const TICKS_PER_DAY = 600;
export const DAYS_PER_SEASON = 30;
export const DAYS_PER_YEAR = 120;
export const WEATHER_INTERVAL_TICKS = 10;
export const NEED_MAX = 100;
export const WORLD_SNAPSHOT_INTERVAL_TICKS = 5;
export const UI_SNAPSHOT_INTERVAL_MS = 250;
export const UI_STATE_INTERVAL_MS = 100;
export const EVENT_HISTORY_LIMIT = 250;

export const NEED_DECAY_PER_TICK = {
  food: 0.05,
  rest: 0.06,
  water: 0.08,
} as const;

export const MOVEMENT_INTERVAL_TICKS = 2;
export const GATHER_INTERVAL_TICKS = 5;
export const RESOURCE_UNITS_PER_GATHER = 1;
export const PREY_PER_CONTINENT = 3;
export const PREDATOR_PER_CONTINENT = 1;
export const PREDATOR_HUNT_RADIUS = 12;
export const PREDATOR_ATTACK_RANGE = 1;
export const HUMANOID_FLEE_RADIUS = 6;
export const HEALTH_MAX = 100;
export const INJURY_DURATION_TICKS = 250;
export const RESOURCE_RENEWAL_INTERVAL_TICKS = 100;
export const HEALTH_RECOVERY_INTERVAL_TICKS = 50;
export const FRESHWATER_MOISTURE = 0.88;
export const RIDGE_ELEVATION = 0.78;
export const GESTATION_DAYS = 8;
export const CONCEPTION_CHANCE = 0.55;
export const PAIR_DISTANCE_TILES = 10;
export const REPRODUCTIVE_AGE_MIN = 16;
export const REPRODUCTIVE_AGE_MAX = 45;

export const DEFAULT_WORLD_SEED = 0x1f1ef1a5;

export type ContinentId = "west" | "east";
export type TerrainType =
  "deep-water" | "grassland" | "forest" | "rough-ground";
export type ResourceFamily = "water" | "food" | "wood" | "stone";
export type Sex = "male" | "female";
export type LifeStage = "child" | "adolescent" | "adult" | "elder";
export type SpeciesKind = "humanoid" | "herbivore" | "predator";
export type CreatureAction =
  | "fleeing"
  | "gathering"
  | "grazing"
  | "hunting"
  | "idle"
  | "moving"
  | "resting";
export type CreatureJob = "forager" | "hunter" | "none" | "water-gatherer";
export const BUILDING_KINDS = [
  "hearth",
  "shelter",
  "storage",
  "work-surface",
] as const;
export type BuildingKind = (typeof BUILDING_KINDS)[number];
export type MoodLabel =
  | "afraid"
  | "content"
  | "exhausted"
  | "hungry"
  | "injured"
  | "thirsty"
  | "uneasy";
export type SeasonName = "autumn" | "spring" | "summer" | "winter";
export type Precipitation = "clear" | "rain" | "snow";
export const SEASON_NAMES = [
  "spring",
  "summer",
  "autumn",
  "winter",
] as const satisfies readonly SeasonName[];

export function seasonNameFromDay(day: number): SeasonName {
  const index = Math.floor(day / DAYS_PER_SEASON) % SEASON_NAMES.length;
  return SEASON_NAMES[index] ?? "spring";
}

export type HazardType = "cold_snap" | "drought" | "famine" | "storm";
export type PromotedEventType =
  | "birth"
  | "death"
  | "hazard-ended"
  | "hazard-started"
  | "household-formed"
  | "injury"
  | "predator-attack"
  | "recovery";
export type EventSeverity = "ambient" | "critical" | "notable";
export type DeathCause =
  "dehydration" | "exposure" | "hazard" | "injury" | "predator" | "starvation";

export interface TraitVector {
  aggression: number;
  intelligence: number;
  riskTolerance: number;
  strength: number;
}

export interface PopulationProfile {
  id: string;
  label: string;
  means: TraitVector;
}

export interface TileRecord {
  continentId: ContinentId | null;
  elevation: number;
  id: number;
  moisture: number;
  terrain: TerrainType;
  walkable: boolean;
  x: number;
  y: number;
}

export interface ResourceNode {
  capacity: number;
  continentId: ContinentId;
  family: ResourceFamily;
  id: string;
  quality: number;
  quantity: number;
  renewal: number;
  subtype: string;
  tileId: number;
  variantId: string;
}

export interface SpeciesDefinition {
  continentId: ContinentId | null;
  diet: "carnivore" | "omnivore" | "herbivore";
  id: string;
  kind: SpeciesKind;
  name: string;
}

export interface CreatureNeeds {
  food: number;
  rest: number;
  safety: number;
  warmth: number;
  water: number;
}

export interface CreatureInventory {
  food: number;
  water: number;
}

export interface CreatureMood {
  label: MoodLabel;
  reason: string;
  score: number;
}

export interface DecisionRecord {
  action: CreatureAction;
  reason: string;
  targetResourceNodeId: string | null;
  targetTileId: number | null;
  tick: number;
}

export interface CreatureCondition {
  cause: DeathCause;
  id: string;
  kind: "exposure" | "injury";
  remainingTicks: number;
  sourceCreatureId: string | null;
}

export interface DeathRecord {
  cause: DeathCause;
  eventId: string;
  tick: number;
}

export interface EventRecord {
  actorIds: string[];
  causeData: Record<string, boolean | number | string | null>;
  causeIds: string[];
  continentId: ContinentId | null;
  displayDate: { day: number; year: number };
  effectData: Record<string, boolean | number | string | null>;
  groupingKey: string;
  id: string;
  location: { tileId: number | null };
  promotedBecause: string[];
  retention: "history";
  schemaVersion: 1;
  severity: EventSeverity;
  targetIds: string[];
  tick: number;
  type: PromotedEventType;
}

export interface HazardInstance {
  active: boolean;
  continentId: ContinentId | null;
  endTick: number;
  id: string;
  scheduleId: string;
  startTick: number;
  tileIds: number[];
  type: HazardType;
}

export interface PregnancyState {
  conceivedDay: number;
  fatherId: string;
  remainingDays: number;
}

export interface HouseholdRecord {
  continentId: ContinentId;
  id: string;
  memberIds: string[];
  tileId: number;
}

export interface CreatureRecord {
  action: CreatureAction;
  ageYears: number;
  alive: boolean;
  conditions: CreatureCondition[];
  continentId: ContinentId;
  currentJob: CreatureJob;
  currentTargetResourceNodeId: string | null;
  currentTargetTileId: number | null;
  death: DeathRecord | null;
  gatherCooldownTicks: number;
  health: number;
  householdId: string | null;
  id: string;
  inventory: CreatureInventory;
  kind: SpeciesKind;
  lifeStage: LifeStage;
  movementCooldownTicks: number;
  name: string;
  needs: CreatureNeeds;
  mood: CreatureMood;
  parentIds: string[];
  pregnancy: PregnancyState | null;
  profileId: string;
  recentDecisions: DecisionRecord[];
  sex: Sex;
  speciesId: string;
  tileId: number;
  traits: TraitVector;
}

export interface ContinentRecord {
  id: ContinentId;
  populationProfileId: string;
  resourceNodeIds: string[];
  side: "east" | "west";
  startingTileId: number;
  tileIds: number[];
}

export interface CalendarState {
  day: number;
  seasonIndex: number;
  tick: number;
  year: number;
}

export interface WeatherState {
  intensity: number;
  precipitation: Precipitation;
  season: SeasonName;
  temperature: number;
}

export interface PlayerState {
  followedCreatureId: string | null;
  selectedCreatureId: string | null;
}

export interface StartSite {
  continentId: ContinentId;
  id: string;
  predatorDenId: string;
  preyRangeId: string;
  protectedRadius: number;
  tileId: number;
}

export interface BuildingRecord {
  continentId: ContinentId;
  id: string;
  kind: BuildingKind;
  label: string;
  making: string;
  purpose: string;
  storedUnits: number;
  tileId: number;
  workerCreatureIds: string[];
}

export interface WildlifeSite {
  continentId: ContinentId;
  id: string;
  kind: "predator-den" | "prey-range";
  speciesId: string;
  tileId: number;
}

export interface HazardSchedule {
  continentId: ContinentId | null;
  durationDays: number;
  id: string;
  triggerDay: number;
  type: HazardType;
}

export interface WorldDiagnostics {
  continentTileCounts: Record<ContinentId, number>;
  generatorVersion: string;
  landTileCount: number;
  resourceOpportunity: Record<ResourceFamily, Record<ContinentId, number>>;
}

export interface WorldState {
  activeHazards: HazardInstance[];
  buildings: BuildingRecord[];
  calendar: CalendarState;
  continents: ContinentRecord[];
  creatures: CreatureRecord[];
  diagnostics: WorldDiagnostics;
  events: EventRecord[];
  groups: unknown[];
  hazardSchedules: HazardSchedule[];
  households: HouseholdRecord[];
  inventories: unknown[];
  knowledgeRecords: unknown[];
  playerState: PlayerState;
  populationProfiles: PopulationProfile[];
  resourceNodes: ResourceNode[];
  schemaVersion: number;
  seed: number;
  speciesDefinitions: SpeciesDefinition[];
  startSites: StartSite[];
  tick: number;
  weather: WeatherState;
  tiles: TileRecord[];
  wildlifeSites: WildlifeSite[];
  width: number;
  height: number;
  rulesetVersion: string;
}

interface ResourceConfig {
  baseCapacity: number;
  count: number;
  family: ResourceFamily;
  preferredTerrain: TerrainType[];
  renewal: number;
  subtype: string;
}

interface AgeBand {
  ageMax: number;
  ageMin: number;
  count: number;
  lifeStage: LifeStage;
  sex: Sex;
}

const CONTINENTS: readonly ContinentId[] = ["west", "east"];

const RESOURCE_CONFIGS: readonly ResourceConfig[] = [
  {
    baseCapacity: 100,
    count: 5,
    family: "water",
    preferredTerrain: ["grassland", "rough-ground", "forest"],
    renewal: 0.9,
    subtype: "spring",
  },
  {
    baseCapacity: 90,
    count: 8,
    family: "food",
    preferredTerrain: ["grassland", "forest"],
    renewal: 0.14,
    subtype: "edible-plants",
  },
  {
    baseCapacity: 80,
    count: 12,
    family: "wood",
    preferredTerrain: ["forest"],
    renewal: 0.04,
    subtype: "forest-trees",
  },
  {
    baseCapacity: 100,
    count: 6,
    family: "stone",
    preferredTerrain: ["rough-ground"],
    renewal: 0,
    subtype: "stone-outcrop",
  },
];

const AGE_BANDS: readonly AgeBand[] = [
  { ageMax: 45, ageMin: 16, count: 8, lifeStage: "adult", sex: "male" },
  { ageMax: 45, ageMin: 16, count: 8, lifeStage: "adult", sex: "female" },
  {
    ageMax: 15,
    ageMin: 10,
    count: 2,
    lifeStage: "adolescent",
    sex: "male",
  },
  {
    ageMax: 15,
    ageMin: 10,
    count: 2,
    lifeStage: "adolescent",
    sex: "female",
  },
  { ageMax: 9, ageMin: 0, count: 1, lifeStage: "child", sex: "male" },
  { ageMax: 9, ageMin: 0, count: 1, lifeStage: "child", sex: "female" },
  { ageMax: 75, ageMin: 46, count: 1, lifeStage: "elder", sex: "male" },
  { ageMax: 75, ageMin: 46, count: 1, lifeStage: "elder", sex: "female" },
];

const BUILDING_CATALOG: Record<
  BuildingKind,
  { idleMaking: string; label: string; purpose: string }
> = {
  hearth: {
    idleMaking: "Banked fire and warmth",
    label: "Fire pit",
    purpose: "Heat, cooking, and fuel work",
  },
  shelter: {
    idleMaking: "Rest and household warmth",
    label: "Lean-to",
    purpose: "Warmth, rest, and household cover",
  },
  storage: {
    idleMaking: "Empty stores",
    label: "Storage rack",
    purpose: "Protect gathered food and water",
  },
  "work-surface": {
    idleMaking: "Waiting for stone and wood",
    label: "Work stone",
    purpose: "Shape stone, wood, and fiber",
  },
};

const CAMP_BUILDING_ORDER = [
  "shelter",
  "hearth",
  "storage",
  "work-surface",
] as const satisfies readonly BuildingKind[];

const POPULATION_PROFILES: readonly PopulationProfile[] = [
  {
    id: "west-aggressive",
    label: "West / aggressive",
    means: {
      aggression: 0.75,
      intelligence: 0.35,
      riskTolerance: 0.78,
      strength: 0.7,
    },
  },
  {
    id: "east-planner",
    label: "East / planner",
    means: {
      aggression: 0.25,
      intelligence: 0.75,
      riskTolerance: 0.3,
      strength: 0.7,
    },
  },
];

const NAME_FIRST_PARTS = [
  "Ari",
  "Bren",
  "Cala",
  "Daro",
  "Ena",
  "Fera",
  "Garo",
  "Iri",
  "Jora",
  "Kelan",
  "Luma",
  "Mira",
  "Naro",
  "Oren",
  "Pela",
  "Rava",
];

const NAME_SECOND_PARTS = [
  "dan",
  "vek",
  "ra",
  "tal",
  "mon",
  "esh",
  "un",
  "ka",
  "rin",
  "vor",
  "el",
  "sa",
  "kor",
  "in",
  "um",
  "ay",
];

const SPECIES_FIRST_PARTS = [
  "Aru",
  "Bela",
  "Kora",
  "Moru",
  "Nava",
  "Oru",
  "Sela",
  "Taru",
];

const SPECIES_SECOND_PARTS = [
  "back",
  "horn",
  "maw",
  "runner",
  "stalker",
  "grazer",
  "crest",
  "tail",
];

function sortById<T extends { id: string | number }>(records: T[]) {
  return records.sort((left, right) => {
    const leftId = String(left.id);
    const rightId = String(right.id);
    return leftId < rightId ? -1 : leftId > rightId ? 1 : 0;
  });
}

function round(value: number, decimals = 3) {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

function clamp(value: number, minimum = 0, maximum = 1) {
  return Math.min(maximum, Math.max(minimum, value));
}

export function deriveSeed(seed: number, stream: string) {
  if (!Number.isInteger(seed)) {
    throw new TypeError("World seeds must be integers.");
  }

  let hash = (0x811c9dc5 ^ (seed >>> 0)) >>> 0;
  for (let index = 0; index < stream.length; index += 1) {
    hash ^= stream.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }

  return hash >>> 0;
}

function continentForX(x: number, westEnd: number, eastStart: number) {
  if (x <= westEnd) return "west" as const;
  if (x >= eastStart) return "east" as const;
  return null;
}

function terrainForVariation(variation: number): TerrainType {
  if (variation < 0.48) return "grassland";
  if (variation < 0.82) return "forest";
  return "rough-ground";
}

function buildTiles(seed: number) {
  const coastlineRandom = new SeededRandom(deriveSeed(seed, "coastline"));
  const terrainRandom = new SeededRandom(deriveSeed(seed, "terrain"));
  const tiles: TileRecord[] = [];

  for (let y = 0; y < WORLD_HEIGHT; y += 1) {
    const westEnd = 38 + coastlineRandom.nextInt(0, 6);
    const eastStart = 52 + coastlineRandom.nextInt(0, 6);

    for (let x = 0; x < WORLD_WIDTH; x += 1) {
      const id = y * WORLD_WIDTH + x;
      const isBorder =
        x === 0 || y === 0 || x === WORLD_WIDTH - 1 || y === WORLD_HEIGHT - 1;
      const continentId = isBorder
        ? null
        : continentForX(x, westEnd, eastStart);
      const elevation = round(terrainRandom.nextFloat());
      const moisture = round(terrainRandom.nextFloat());
      const terrain = continentId
        ? terrainForVariation(terrainRandom.nextFloat())
        : "deep-water";

      tiles.push({
        continentId,
        elevation,
        id,
        moisture,
        terrain,
        walkable: continentId !== null,
        x,
        y,
      });
    }
  }

  for (const continentId of CONTINENTS) {
    const continentTiles = tiles.filter(
      (tile) => tile.continentId === continentId,
    );
    const terrainTypes = new Set(continentTiles.map((tile) => tile.terrain));
    const requiredTerrain: TerrainType[] = [
      "grassland",
      "forest",
      "rough-ground",
    ];

    for (const terrain of requiredTerrain) {
      if (terrainTypes.has(terrain)) continue;
      const tile = continentTiles.find(
        (candidate) => candidate.terrain !== "deep-water",
      );
      if (!tile) throw new Error(`No land tile exists on ${continentId}.`);
      tile.terrain = terrain;
      terrainTypes.add(terrain);
    }
  }

  return applyGeography(seed, tiles);
}

function tileAtCoordinate(tiles: TileRecord[], x: number, y: number) {
  if (x < 0 || y < 0 || x >= WORLD_WIDTH || y >= WORLD_HEIGHT) return undefined;
  return tiles[y * WORLD_WIDTH + x];
}

function applyGeography(seed: number, tiles: TileRecord[]) {
  const smoothed = tiles.map((tile) => tile.elevation);
  for (const tile of tiles) {
    if (!tile.walkable) continue;
    const neighbors = (
      [
        [0, -1],
        [1, 0],
        [0, 1],
        [-1, 0],
      ] as [number, number][]
    )
      .map(([dx, dy]) => tileAtCoordinate(tiles, tile.x + dx, tile.y + dy))
      .filter((neighbor): neighbor is TileRecord =>
        Boolean(neighbor?.walkable),
      );
    if (neighbors.length === 0) continue;
    const average =
      neighbors.reduce((total, neighbor) => total + neighbor.elevation, 0) /
      neighbors.length;
    smoothed[tile.id] = round(tile.elevation * 0.55 + average * 0.45);
  }
  for (const tile of tiles) {
    const nextElevation = smoothed[tile.id];
    if (tile.walkable && nextElevation !== undefined) {
      tile.elevation = nextElevation;
    }
  }

  const directions: [number, number][] = [
    [0, -1],
    [1, 0],
    [0, 1],
    [-1, 0],
  ];

  for (const continentId of CONTINENTS) {
    const random = new SeededRandom(
      deriveSeed(seed, `geography:${continentId}`),
    );
    const land = tiles.filter((tile) => tile.continentId === continentId);
    const byElevation = [...land].sort(
      (left, right) => right.elevation - left.elevation || left.id - right.id,
    );
    const ridgeCount = Math.max(8, Math.floor(land.length * 0.05));
    for (let index = 0; index < ridgeCount; index += 1) {
      const ridge = byElevation[index];
      if (!ridge) continue;
      ridge.elevation = round(Math.min(1, ridge.elevation + 0.2));
      if (ridge.terrain === "grassland") ridge.terrain = "rough-ground";
    }

    const coast = land.filter((tile) =>
      directions.some(([dx, dy]) => {
        const neighbor = tileAtCoordinate(tiles, tile.x + dx, tile.y + dy);
        return neighbor !== undefined && !neighbor.walkable;
      }),
    );
    const inland = land
      .filter((tile) => coast.every((coastTile) => coastTile.id !== tile.id))
      .sort(
        (left, right) => right.elevation - left.elevation || left.id - right.id,
      );
    const source =
      inland[random.nextInt(0, Math.max(1, Math.min(12, inland.length)))];
    const firstCoast = coast[0];
    if (!source || !firstCoast) continue;
    const mouth = coast.reduce((best, tile) => {
      const bestDistance = distanceBetween(best, source);
      const nextDistance = distanceBetween(tile, source);
      if (nextDistance < bestDistance) return tile;
      if (nextDistance === bestDistance && tile.id < best.id) return tile;
      return best;
    }, firstCoast);

    const riverIds = new Set<number>();
    let current: TileRecord | undefined = source;
    let guard = 0;
    while (current && current.id !== mouth.id && guard < 220) {
      riverIds.add(current.id);
      current.moisture = round(Math.max(current.moisture, 0.93));
      current.elevation = round(Math.max(0.08, current.elevation * 0.55));
      const next = directions
        .map(([dx, dy]) =>
          tileAtCoordinate(tiles, current!.x + dx, current!.y + dy),
        )
        .filter(
          (neighbor): neighbor is TileRecord =>
            neighbor !== undefined && neighbor.continentId === continentId,
        )
        .sort((left, right) => {
          const distanceDifference =
            distanceBetween(left, mouth) - distanceBetween(right, mouth);
          return distanceDifference || left.id - right.id;
        })[0];
      if (!next || next.id === current.id) break;
      current = next;
      guard += 1;
    }
    riverIds.add(mouth.id);
    mouth.moisture = round(Math.max(mouth.moisture, 0.94));

    for (const tileId of riverIds) {
      const tile = tiles[tileId];
      if (!tile) continue;
      for (const [dx, dy] of directions) {
        const neighbor = tileAtCoordinate(tiles, tile.x + dx, tile.y + dy);
        if (
          neighbor &&
          neighbor.continentId === continentId &&
          random.nextFloat() < 0.34
        ) {
          neighbor.moisture = round(Math.max(neighbor.moisture, 0.89));
        }
      }
    }

    const riverTiles = [...riverIds]
      .map((tileId) => tiles[tileId])
      .filter((tile): tile is TileRecord => tile !== undefined);
    const lakeCenter = riverTiles[Math.floor(riverTiles.length / 2)];
    if (lakeCenter) {
      for (const tile of land) {
        if (distanceBetween(tile, lakeCenter) <= 2) {
          tile.moisture = round(Math.max(tile.moisture, 0.96));
          tile.elevation = round(Math.min(tile.elevation, 0.22));
        }
      }
    }

    for (const tile of land) {
      if (
        tile.moisture >= 0.7 &&
        tile.moisture < FRESHWATER_MOISTURE &&
        tile.terrain === "grassland" &&
        tile.elevation < 0.7
      ) {
        tile.terrain = "forest";
      }
    }
  }

  return tiles;
}

function chooseStartingTile(
  tiles: TileRecord[],
  continentId: ContinentId,
  targetX: number,
  targetY: number,
) {
  const candidates = tiles.filter((tile) => tile.continentId === continentId);
  const startingTile = candidates.reduce((closest, candidate) => {
    const closestDistance =
      Math.abs(closest.x - targetX) + Math.abs(closest.y - targetY);
    const candidateDistance =
      Math.abs(candidate.x - targetX) + Math.abs(candidate.y - targetY);
    return candidateDistance < closestDistance ? candidate : closest;
  });
  return startingTile.id;
}

function distanceBetween(left: TileRecord, right: TileRecord) {
  return Math.abs(left.x - right.x) + Math.abs(left.y - right.y);
}

function pickResourceTile(
  tiles: TileRecord[],
  continentId: ContinentId,
  startingTile: TileRecord,
  preferredTerrain: TerrainType[],
  random: SeededRandom,
  usedTileIds: Set<number>,
  prioritizeStartingRegion: boolean,
) {
  const continentTiles = tiles.filter(
    (tile) => tile.continentId === continentId && !usedTileIds.has(tile.id),
  );
  const preferredTiles = continentTiles.filter((tile) =>
    preferredTerrain.includes(tile.terrain),
  );
  const sourceTiles =
    preferredTiles.length > 0 ? preferredTiles : continentTiles;
  const nearbyTiles = sourceTiles.filter(
    (tile) => distanceBetween(tile, startingTile) <= 8,
  );
  const nearbyAnyTerrainTiles = continentTiles.filter(
    (tile) => distanceBetween(tile, startingTile) <= 8,
  );
  const candidates =
    prioritizeStartingRegion && nearbyTiles.length > 0
      ? nearbyTiles
      : prioritizeStartingRegion && nearbyAnyTerrainTiles.length > 0
        ? nearbyAnyTerrainTiles
        : sourceTiles.length > 0
          ? sourceTiles
          : continentTiles;

  if (candidates.length === 0) {
    throw new Error(`No resource placement tile exists on ${continentId}.`);
  }

  const tile = candidates[random.nextInt(0, candidates.length)];
  if (!tile) throw new Error(`Resource placement failed on ${continentId}.`);
  usedTileIds.add(tile.id);
  return tile;
}

function placeResources(
  seed: number,
  tiles: TileRecord[],
  startingTiles: Record<ContinentId, TileRecord>,
) {
  const resourceNodes: ResourceNode[] = [];
  const resourceNodeIds: Record<ContinentId, string[]> = {
    east: [],
    west: [],
  };
  const usedTileIds: Record<ContinentId, Set<number>> = {
    east: new Set(),
    west: new Set(),
  };

  for (const config of RESOURCE_CONFIGS) {
    const pairRandom = new SeededRandom(
      deriveSeed(seed, `resource-pair:${config.family}`),
    );
    const locationRandoms: Record<ContinentId, SeededRandom> = {
      east: new SeededRandom(
        deriveSeed(seed, `resource-location:east:${config.family}`),
      ),
      west: new SeededRandom(
        deriveSeed(seed, `resource-location:west:${config.family}`),
      ),
    };

    for (let index = 0; index < config.count; index += 1) {
      const capacity = Math.round(
        config.baseCapacity * (0.9 + pairRandom.nextFloat() * 0.2),
      );
      const quality = round(0.55 + pairRandom.nextFloat() * 0.2);

      for (const continentId of CONTINENTS) {
        const tile = pickResourceTile(
          tiles,
          continentId,
          startingTiles[continentId],
          config.preferredTerrain,
          locationRandoms[continentId],
          usedTileIds[continentId],
          index === 0,
        );
        const id = `${continentId}-${config.family}-${index}`;
        const node: ResourceNode = {
          capacity,
          continentId,
          family: config.family,
          id,
          quality,
          quantity: capacity,
          renewal: config.renewal,
          subtype: config.subtype,
          tileId: tile.id,
          variantId: config.subtype,
        };

        resourceNodes.push(node);
        resourceNodeIds[continentId].push(id);
      }
    }
  }

  return { resourceNodeIds, resourceNodes: sortById(resourceNodes) };
}

function createGeneratedSpeciesName(
  random: SeededRandom,
  usedNames: Set<string>,
) {
  let name = "";
  while (!name || usedNames.has(name)) {
    const first =
      SPECIES_FIRST_PARTS[random.nextInt(0, SPECIES_FIRST_PARTS.length)];
    const second =
      SPECIES_SECOND_PARTS[random.nextInt(0, SPECIES_SECOND_PARTS.length)];
    name = `${first}${second}`;
  }
  usedNames.add(name);
  return name;
}

function createSpeciesDefinitions(seed: number) {
  const random = new SeededRandom(deriveSeed(seed, "species-names"));
  const usedNames = new Set<string>();
  const definitions: SpeciesDefinition[] = [
    {
      continentId: null,
      diet: "omnivore",
      id: "humanoid",
      kind: "humanoid",
      name: "Humanoid",
    },
  ];

  for (const continentId of CONTINENTS) {
    definitions.push(
      {
        continentId,
        diet: "herbivore",
        id: `${continentId}-herbivore`,
        kind: "herbivore",
        name: createGeneratedSpeciesName(random, usedNames),
      },
      {
        continentId,
        diet: "carnivore",
        id: `${continentId}-predator`,
        kind: "predator",
        name: createGeneratedSpeciesName(random, usedNames),
      },
    );
  }

  return sortById(definitions);
}

function chooseWildlifeTile(
  tiles: TileRecord[],
  continentId: ContinentId,
  startingTile: TileRecord,
  random: SeededRandom,
  minimumDistance: number,
  maximumDistance?: number,
) {
  const candidates = tiles.filter((tile) => {
    if (tile.continentId !== continentId) return false;
    const distance = distanceBetween(tile, startingTile);
    return (
      distance >= minimumDistance &&
      (maximumDistance === undefined || distance <= maximumDistance)
    );
  });
  if (candidates.length === 0) {
    throw new Error(`No wildlife site exists on ${continentId}.`);
  }
  const tile = candidates[random.nextInt(0, candidates.length)];
  if (!tile) throw new Error(`Wildlife placement failed on ${continentId}.`);
  return tile;
}

function createWorldSites(
  seed: number,
  tiles: TileRecord[],
  startingTiles: Record<ContinentId, TileRecord>,
) {
  const startSites: StartSite[] = [];
  const wildlifeSites: WildlifeSite[] = [];
  const random = new SeededRandom(deriveSeed(seed, "wildlife-sites"));

  for (const continentId of CONTINENTS) {
    const startingTile = startingTiles[continentId];
    const preyTile = chooseWildlifeTile(
      tiles,
      continentId,
      startingTile,
      random,
      3,
      8,
    );
    const predatorTile = chooseWildlifeTile(
      tiles,
      continentId,
      startingTile,
      random,
      14,
    );
    const preyRangeId = `${continentId}-prey-range`;
    const predatorDenId = `${continentId}-predator-den`;

    wildlifeSites.push(
      {
        continentId,
        id: preyRangeId,
        kind: "prey-range",
        speciesId: `${continentId}-herbivore`,
        tileId: preyTile.id,
      },
      {
        continentId,
        id: predatorDenId,
        kind: "predator-den",
        speciesId: `${continentId}-predator`,
        tileId: predatorTile.id,
      },
    );
    startSites.push({
      continentId,
      id: `${continentId}-start`,
      predatorDenId,
      preyRangeId,
      protectedRadius: 8,
      tileId: startingTile.id,
    });
  }

  return {
    hazardSchedules: [
      {
        continentId: null,
        durationDays: 2,
        id: "hazard-storm-0",
        triggerDay: 16,
        type: "storm" as const,
      },
      {
        continentId: null,
        durationDays: 4,
        id: "hazard-cold-snap-0",
        triggerDay: 90,
        type: "cold_snap" as const,
      },
      {
        continentId: null,
        durationDays: 6,
        id: "hazard-drought-0",
        triggerDay: 150,
        type: "drought" as const,
      },
      {
        continentId: null,
        durationDays: 8,
        id: "hazard-famine-0",
        triggerDay: 240,
        type: "famine" as const,
      },
    ] satisfies HazardSchedule[],
    startSites: sortById(startSites),
    wildlifeSites: sortById(wildlifeSites),
  };
}

function adjacentWalkableTiles(tiles: TileRecord[], origin: TileRecord) {
  const directions: [number, number][] = [
    [0, -1],
    [1, 0],
    [0, 1],
    [-1, 0],
  ];
  return directions
    .map(([dx, dy]) => tiles[(origin.y + dy) * WORLD_WIDTH + (origin.x + dx)])
    .filter(
      (tile): tile is TileRecord =>
        tile !== undefined &&
        tile.continentId === origin.continentId &&
        tile.walkable,
    );
}

function createCampBuildings(
  tiles: TileRecord[],
  startSites: StartSite[],
): BuildingRecord[] {
  const buildings: BuildingRecord[] = [];

  for (const site of startSites) {
    const origin =
      tiles[site.tileId] ?? tiles.find((tile) => tile.id === site.tileId);
    if (!origin) continue;
    const neighbors = adjacentWalkableTiles(tiles, origin);
    CAMP_BUILDING_ORDER.forEach((kind, index) => {
      const tile = index === 0 ? origin : (neighbors[index - 1] ?? origin);
      const catalog = BUILDING_CATALOG[kind];
      buildings.push({
        continentId: site.continentId,
        id: `${site.continentId}-${kind}`,
        kind,
        label: catalog.label,
        making: catalog.idleMaking,
        purpose: catalog.purpose,
        storedUnits: 0,
        tileId: tile.id,
        workerCreatureIds: [],
      });
    });
  }

  return sortById(buildings);
}

function wildlifeTraits(kind: "herbivore" | "predator"): TraitVector {
  if (kind === "predator") {
    return {
      aggression: 0.86,
      intelligence: 0.42,
      riskTolerance: 0.74,
      strength: 0.82,
    };
  }

  return {
    aggression: 0.12,
    intelligence: 0.22,
    riskTolerance: 0.18,
    strength: 0.34,
  };
}

function createWildlifeCreatures(
  seed: number,
  tiles: TileRecord[],
  wildlifeSites: WildlifeSite[],
  speciesDefinitions: SpeciesDefinition[],
) {
  const creatures: CreatureRecord[] = [];
  const random = new SeededRandom(deriveSeed(seed, "wildlife-creatures"));

  for (const site of sortById([...wildlifeSites])) {
    const species = speciesDefinitions.find(
      (definition) => definition.id === site.speciesId,
    );
    if (!species) continue;
    const home = tiles.find((tile) => tile.id === site.tileId);
    if (!home) continue;
    const nearby = tiles.filter(
      (tile) =>
        tile.continentId === site.continentId &&
        isWalkableTile(tile) &&
        distanceBetween(tile, home) <= 3,
    );
    const count =
      site.kind === "prey-range" ? PREY_PER_CONTINENT : PREDATOR_PER_CONTINENT;
    const kind = site.kind === "prey-range" ? "herbivore" : "predator";

    for (let index = 0; index < count; index += 1) {
      const tile =
        nearby[random.nextInt(0, Math.max(1, nearby.length))] ?? home;
      const status = initialCreatureStatus(random, "adult");
      creatures.push({
        ...status,
        action: kind === "herbivore" ? "grazing" : "idle",
        ageYears: kind === "herbivore" ? 4 + index : 7,
        alive: true,
        conditions: [],
        continentId: site.continentId,
        death: null,
        health: HEALTH_MAX,
        id: `${site.id}-${String(index + 1).padStart(2, "0")}`,
        kind,
        lifeStage: "adult",
        name: `${species.name} ${index + 1}`,
        profileId: "wildlife",
        sex: index % 2 === 0 ? "female" : "male",
        speciesId: species.id,
        tileId: tile.id,
        traits: wildlifeTraits(kind),
      });
    }
  }

  return sortById(creatures);
}

function createGeneratedCreatureName(
  random: SeededRandom,
  usedNames: Set<string>,
) {
  let name = "";
  while (!name || usedNames.has(name)) {
    const first = NAME_FIRST_PARTS[random.nextInt(0, NAME_FIRST_PARTS.length)];
    const second =
      NAME_SECOND_PARTS[random.nextInt(0, NAME_SECOND_PARTS.length)];
    name = `${first}${second}`;
  }
  usedNames.add(name);
  return name;
}

function createTraits(profile: PopulationProfile, random: SeededRandom) {
  const variation = () => random.nextFloat() * 0.24 - 0.12;
  return {
    aggression: round(clamp(profile.means.aggression + variation())),
    intelligence: round(clamp(profile.means.intelligence + variation())),
    riskTolerance: round(clamp(profile.means.riskTolerance + variation())),
    strength: round(clamp(profile.means.strength + variation())),
  } satisfies TraitVector;
}

function createInitialNeeds(random: SeededRandom, lifeStage: LifeStage) {
  const restBaseline = lifeStage === "child" || lifeStage === "elder" ? 68 : 76;
  return {
    food: round(62 + random.nextFloat() * 25),
    rest: round(restBaseline + random.nextFloat() * 20),
    safety: round(74 + random.nextFloat() * 18),
    warmth: round(70 + random.nextFloat() * 20),
    water: round(60 + random.nextFloat() * 28),
  } satisfies CreatureNeeds;
}

function moodFor(
  creature: Pick<
    CreatureRecord,
    "action" | "alive" | "conditions" | "health" | "needs"
  >,
) {
  const { food, rest, safety, warmth, water } = creature.needs;
  const average = (food + rest + water + warmth + safety) / 5;
  const lowest = Math.min(food, rest, water);
  let label: MoodLabel = "content";
  let reason = "Needs are stable.";

  if (!creature.alive) {
    label = "injured";
    reason = "This creature is no longer alive.";
  } else if (
    creature.health < 35 ||
    creature.conditions.some((condition) => condition.kind === "injury")
  ) {
    label = "injured";
    reason = "An injury is draining strength and health.";
  } else if (safety < 32) {
    label = "afraid";
    reason = "A nearby predator or hazard is making this place unsafe.";
  } else if (lowest < 22) {
    label =
      water === lowest ? "thirsty" : food === lowest ? "hungry" : "exhausted";
    reason =
      water === lowest
        ? "Water is critically low."
        : food === lowest
          ? "Food is critically low."
          : "Rest is critically low.";
  } else if (lowest < 42) {
    label =
      water === lowest ? "thirsty" : food === lowest ? "hungry" : "exhausted";
    reason =
      water === lowest
        ? "Water is becoming a concern."
        : food === lowest
          ? "Food is becoming a concern."
          : "Rest is becoming a concern.";
  } else if (average < 62) {
    label = "uneasy";
    reason = "Several needs are below comfort.";
  } else if (creature.action === "resting") {
    label = "uneasy";
    reason = "Recovering before returning to work.";
  }

  return {
    label,
    reason,
    score: round(clamp(average / NEED_MAX)),
  } satisfies CreatureMood;
}

function initialCreatureStatus(random: SeededRandom, lifeStage: LifeStage) {
  const needs = createInitialNeeds(random, lifeStage);
  const status = {
    action: "idle" as const,
    alive: true,
    conditions: [] as CreatureCondition[],
    currentJob: "none" as const,
    currentTargetResourceNodeId: null,
    currentTargetTileId: null,
    householdId: null,
    gatherCooldownTicks: 0,
    health: HEALTH_MAX,
    inventory: { food: 0, water: 0 },
    movementCooldownTicks: 0,
    parentIds: [] as string[],
    pregnancy: null as PregnancyState | null,
    needs,
    mood: {
      label: "content" as MoodLabel,
      reason: "Needs are stable.",
      score: 0,
    },
    recentDecisions: [],
  };
  status.mood = moodFor(status);
  return status;
}

function createCreatures(
  seed: number,
  tiles: TileRecord[],
  startingTiles: Record<ContinentId, TileRecord>,
) {
  const creatures: CreatureRecord[] = [];
  const usedNames = new Set<string>();

  for (const continentId of CONTINENTS) {
    const profile = POPULATION_PROFILES.find(
      (candidate) =>
        candidate.id ===
        (continentId === "west" ? "west-aggressive" : "east-planner"),
    );
    if (!profile) throw new Error(`Missing profile for ${continentId}.`);

    const random = new SeededRandom(
      deriveSeed(seed, `population:${continentId}`),
    );
    const positionTiles = tiles.filter(
      (tile) =>
        tile.continentId === continentId &&
        distanceBetween(tile, startingTiles[continentId]) <= 5,
    );

    let creatureIndex = 0;
    for (const ageBand of AGE_BANDS) {
      for (let count = 0; count < ageBand.count; count += 1) {
        const tile =
          positionTiles[random.nextInt(0, positionTiles.length)] ??
          startingTiles[continentId];
        const status = initialCreatureStatus(random, ageBand.lifeStage);
        creatures.push({
          ...status,
          ageYears: random.nextInt(ageBand.ageMin, ageBand.ageMax + 1),
          alive: true,
          conditions: [],
          continentId,
          death: null,
          health: HEALTH_MAX,
          id: `${continentId}-humanoid-${String(creatureIndex + 1).padStart(2, "0")}`,
          kind: "humanoid",
          lifeStage: ageBand.lifeStage,
          name: createGeneratedCreatureName(random, usedNames),
          profileId: profile.id,
          sex: ageBand.sex,
          speciesId: "humanoid",
          tileId: tile.id,
          traits: createTraits(profile, random),
        });
        creatureIndex += 1;
      }
    }
  }

  return sortById(creatures);
}

function resourceOpportunity(node: ResourceNode) {
  return round(node.quantity * (0.7 + node.quality * 0.3) + node.renewal * 100);
}

function createDiagnostics(
  continents: ContinentRecord[],
  resourceNodes: ResourceNode[],
): WorldDiagnostics {
  const resourceOpportunity = {} as WorldDiagnostics["resourceOpportunity"];
  for (const family of RESOURCE_CONFIGS.map((config) => config.family)) {
    resourceOpportunity[family] = { east: 0, west: 0 };
    for (const continentId of CONTINENTS) {
      resourceOpportunity[family][continentId] = resourceNodes
        .filter(
          (node) => node.family === family && node.continentId === continentId,
        )
        .reduce((total, node) => total + resourceOpportunityValue(node), 0);
    }
  }

  return {
    continentTileCounts: {
      east:
        continents.find((continent) => continent.id === "east")?.tileIds
          .length ?? 0,
      west:
        continents.find((continent) => continent.id === "west")?.tileIds
          .length ?? 0,
    },
    generatorVersion: "slice-a-1",
    landTileCount: continents.reduce(
      (total, continent) => total + continent.tileIds.length,
      0,
    ),
    resourceOpportunity,
  };
}

function resourceOpportunityValue(node: ResourceNode) {
  return resourceOpportunity(node);
}

function sortWorldState(world: WorldState) {
  world.tiles.sort((left, right) => left.id - right.id);
  world.continents.sort((left, right) =>
    left.id === right.id ? 0 : left.id === "west" ? -1 : 1,
  );
  sortById(world.resourceNodes);
  sortById(world.speciesDefinitions);
  sortById(world.creatures);
  sortById(world.populationProfiles);
  sortById(world.events);
  sortById(world.activeHazards);
  sortById(world.wildlifeSites);
  sortById(world.hazardSchedules);
  sortById(world.buildings);
  sortById(world.households);
  return world;
}

export function createInitialWorld(seed: number): WorldState {
  if (!Number.isInteger(seed)) {
    throw new TypeError("World seeds must be integers.");
  }
  const normalizedSeed = seed >>> 0;
  const tiles = buildTiles(normalizedSeed);
  const startingTileIds: Record<ContinentId, number> = {
    east: chooseStartingTile(tiles, "east", 73, 32),
    west: chooseStartingTile(tiles, "west", 22, 32),
  };
  const startingTiles = {
    east: tiles.find((tile) => tile.id === startingTileIds.east) as TileRecord,
    west: tiles.find((tile) => tile.id === startingTileIds.west) as TileRecord,
  } satisfies Record<ContinentId, TileRecord>;
  const resourceResult = placeResources(normalizedSeed, tiles, startingTiles);
  const speciesDefinitions = createSpeciesDefinitions(normalizedSeed);
  const worldSites = createWorldSites(normalizedSeed, tiles, startingTiles);
  const continents: ContinentRecord[] = CONTINENTS.map((continentId) => ({
    id: continentId,
    populationProfileId:
      continentId === "west" ? "west-aggressive" : "east-planner",
    resourceNodeIds: resourceResult.resourceNodeIds[continentId].sort(),
    side: continentId,
    startingTileId: startingTileIds[continentId],
    tileIds: tiles
      .filter((tile) => tile.continentId === continentId)
      .map((tile) => tile.id),
  }));
  const world: WorldState = {
    activeHazards: [],
    buildings: createCampBuildings(tiles, worldSites.startSites),
    calendar: { day: 0, seasonIndex: 0, tick: 0, year: 0 },
    continents,
    creatures: [
      ...createCreatures(normalizedSeed, tiles, startingTiles),
      ...createWildlifeCreatures(
        normalizedSeed,
        tiles,
        worldSites.wildlifeSites,
        speciesDefinitions,
      ),
    ],
    diagnostics: createDiagnostics(continents, resourceResult.resourceNodes),
    events: [],
    groups: [],
    hazardSchedules: worldSites.hazardSchedules,
    households: [],
    inventories: [],
    knowledgeRecords: [],
    playerState: { followedCreatureId: null, selectedCreatureId: null },
    populationProfiles: POPULATION_PROFILES.map((profile) => ({
      ...profile,
      means: { ...profile.means },
    })),
    resourceNodes: resourceResult.resourceNodes,
    rulesetVersion: "first-playable-1",
    schemaVersion: WORLD_SCHEMA_VERSION,
    seed: normalizedSeed,
    speciesDefinitions,
    startSites: worldSites.startSites,
    tick: 0,
    weather: {
      intensity: 0.2,
      precipitation: "clear",
      season: "spring",
      temperature: 0.56,
    },
    tiles,
    wildlifeSites: worldSites.wildlifeSites,
    width: WORLD_WIDTH,
    height: WORLD_HEIGHT,
  };

  return sortWorldState(world);
}

export function advanceWorldTime(world: WorldState, ticks: number) {
  if (!Number.isInteger(ticks) || ticks < 0) {
    throw new RangeError("World ticks must be a non-negative integer.");
  }

  world.tick += ticks;
  world.calendar.tick = world.tick;
  world.calendar.day = Math.floor(world.tick / TICKS_PER_DAY);
  world.calendar.seasonIndex =
    Math.floor(world.calendar.day / DAYS_PER_SEASON) % SEASON_NAMES.length;
  world.calendar.year = Math.floor(world.calendar.day / DAYS_PER_YEAR);
}

export function activeHazardTypes(world: WorldState) {
  return world.activeHazards
    .filter((hazard) => hazard.active)
    .map((hazard) => hazard.type);
}

function seasonTemperature(season: SeasonName) {
  switch (season) {
    case "spring":
      return 0.56;
    case "summer":
      return 0.82;
    case "autumn":
      return 0.48;
    case "winter":
      return 0.18;
  }
}

function updateWeather(world: WorldState) {
  const season = seasonNameFromDay(world.calendar.day);
  world.weather.season = season;
  let temperature = seasonTemperature(season);
  const hazards = activeHazardTypes(world);

  if (world.tick % WEATHER_INTERVAL_TICKS === 0) {
    const random = new SeededRandom(
      deriveSeed(
        world.seed,
        `weather:${Math.floor(world.tick / WEATHER_INTERVAL_TICKS)}`,
      ),
    );
    const roll = random.nextFloat();
    const forced = hazards.includes("storm")
      ? {
          intensity: 0.92,
          precipitation: (season === "winter"
            ? "snow"
            : "rain") as Precipitation,
        }
      : hazards.includes("drought")
        ? { intensity: 0.08, precipitation: "clear" as const }
        : null;
    const seasonal: {
      intensity: number;
      precipitation: Precipitation;
    } =
      season === "winter"
        ? {
            intensity: roll < 0.56 ? 0.62 : 0.2,
            precipitation:
              roll < 0.48 ? "snow" : roll < 0.56 ? "rain" : "clear",
          }
        : season === "spring"
          ? {
              intensity: roll < 0.4 ? 0.58 : 0.22,
              precipitation:
                roll < 0.38 ? "rain" : roll < 0.4 ? "snow" : "clear",
            }
          : season === "autumn"
            ? {
                intensity: roll < 0.36 ? 0.55 : 0.2,
                precipitation:
                  roll < 0.3 ? "rain" : roll < 0.36 ? "snow" : "clear",
              }
            : {
                intensity: roll < 0.2 ? 0.5 : 0.16,
                precipitation: roll < 0.2 ? "rain" : "clear",
              };
    const nextWeather = forced ?? seasonal;
    const precipitation = nextWeather.precipitation;
    const intensity = nextWeather.intensity;

    world.weather.precipitation = precipitation;
    world.weather.intensity = intensity;
  }

  if (hazards.includes("cold_snap")) temperature -= 0.22;
  if (hazards.includes("drought")) temperature += 0.12;
  if (world.weather.precipitation === "snow") temperature -= 0.08;
  if (world.weather.precipitation === "rain") temperature -= 0.03;
  world.weather.temperature = round(clamp(temperature));
}

function distanceBetweenTileIds(
  world: WorldState,
  leftId: number,
  rightId: number,
) {
  const left = getTile(world, leftId);
  const right = getTile(world, rightId);
  if (!left || !right) return Number.POSITIVE_INFINITY;
  return distanceBetween(left, right);
}

function setCreatureDecision(
  world: WorldState,
  creature: CreatureRecord,
  decision: Omit<DecisionRecord, "tick"> & {
    action: CreatureAction;
    currentJob: CreatureJob;
  },
) {
  const changed =
    creature.action !== decision.action ||
    creature.currentJob !== decision.currentJob ||
    creature.currentTargetTileId !== decision.targetTileId ||
    creature.currentTargetResourceNodeId !== decision.targetResourceNodeId ||
    creature.recentDecisions[0]?.reason !== decision.reason;

  creature.action = decision.action;
  creature.currentJob = decision.currentJob;
  creature.currentTargetTileId = decision.targetTileId;
  creature.currentTargetResourceNodeId = decision.targetResourceNodeId;

  if (changed) {
    creature.recentDecisions = [
      { ...decision, tick: world.tick },
      ...creature.recentDecisions,
    ].slice(0, 5);
  }
}

function findNearestResource(
  world: WorldState,
  creature: CreatureRecord,
  family: "food" | "water",
) {
  return world.resourceNodes
    .filter(
      (node) =>
        node.continentId === creature.continentId &&
        node.family === family &&
        node.quantity > 0,
    )
    .sort((left, right) => {
      const distanceDifference =
        distanceBetweenTileIds(world, creature.tileId, left.tileId) -
        distanceBetweenTileIds(world, creature.tileId, right.tileId);
      return distanceDifference || (left.id < right.id ? -1 : 1);
    })[0];
}

function stepToward(
  world: WorldState,
  creature: CreatureRecord,
  targetTileId: number,
) {
  const origin = getTile(world, creature.tileId);
  const target = getTile(world, targetTileId);
  if (!origin || !target || origin.id === target.id) return;

  const directions: [number, number][] = [
    [0, -1],
    [1, 0],
    [0, 1],
    [-1, 0],
  ];
  const candidates = directions
    .map(([dx, dy]) =>
      getTile(world, (origin.y + dy) * WORLD_WIDTH + origin.x + dx),
    )
    .filter(
      (tile): tile is TileRecord =>
        tile !== undefined &&
        tile.continentId === creature.continentId &&
        isWalkableTile(tile),
    )
    .filter(
      (tile) => distanceBetween(tile, target) < distanceBetween(origin, target),
    )
    .sort((left, right) => {
      const distanceDifference =
        distanceBetween(left, target) - distanceBetween(right, target);
      return distanceDifference || left.id - right.id;
    });

  const nextTile = candidates[0];
  if (nextTile) creature.tileId = nextTile.id;
}

function consumeHeldResources(creature: CreatureRecord) {
  if (creature.inventory.water > 0 && creature.needs.water < 86) {
    creature.inventory.water -= 1;
    creature.needs.water = round(Math.min(NEED_MAX, creature.needs.water + 28));
  }
  if (creature.inventory.food > 0 && creature.needs.food < 86) {
    creature.inventory.food -= 1;
    creature.needs.food = round(Math.min(NEED_MAX, creature.needs.food + 22));
  }
}

function livingCreatures(world: WorldState) {
  return world.creatures.filter((creature) => creature.alive);
}

export function livingHumanoids(world: WorldState) {
  return livingCreatures(world).filter(
    (creature) => creature.kind === "humanoid",
  );
}

function orderedLiving(world: WorldState) {
  return [...livingCreatures(world)].sort((left, right) =>
    left.id < right.id ? -1 : left.id > right.id ? 1 : 0,
  );
}

function eventDisplayDate(world: WorldState) {
  return { day: world.calendar.day, year: world.calendar.year };
}

function promoteEvent(
  world: WorldState,
  event: Omit<EventRecord, "displayDate" | "id" | "schemaVersion" | "tick"> & {
    id?: string;
  },
) {
  const id =
    event.id ??
    `evt-${world.tick}-${event.type}-${event.actorIds[0] ?? "none"}-${event.targetIds[0] ?? "world"}`;
  if (world.events.some((existing) => existing.id === id)) return;
  world.events.push({
    ...event,
    displayDate: eventDisplayDate(world),
    id,
    schemaVersion: 1,
    tick: world.tick,
  });
  sortById(world.events);
  if (world.events.length > EVENT_HISTORY_LIMIT) {
    world.events.splice(0, world.events.length - EVENT_HISTORY_LIMIT);
  }
}

function hazardAffectsCreature(
  creature: CreatureRecord,
  hazard: HazardInstance,
) {
  if (!hazard.active) return false;
  if (
    hazard.continentId !== null &&
    hazard.continentId !== creature.continentId
  ) {
    return false;
  }
  return hazard.tileIds.includes(creature.tileId);
}

function affectedHazardTiles(world: WorldState, schedule: HazardSchedule) {
  return world.tiles
    .filter((tile) => {
      if (!isWalkableTile(tile)) return false;
      if (schedule.continentId === null) return true;
      return tile.continentId === schedule.continentId;
    })
    .map((tile) => tile.id)
    .sort((left, right) => left - right);
}

function updateHazards(world: WorldState) {
  for (const schedule of [...world.hazardSchedules].sort((left, right) =>
    left.id < right.id ? -1 : left.id > right.id ? 1 : 0,
  )) {
    const shouldBeActive =
      world.calendar.day >= schedule.triggerDay &&
      world.calendar.day < schedule.triggerDay + schedule.durationDays;
    let instance = world.activeHazards.find(
      (hazard) => hazard.scheduleId === schedule.id,
    );

    if (shouldBeActive && !instance) {
      instance = {
        active: true,
        continentId: schedule.continentId,
        endTick:
          (schedule.triggerDay + schedule.durationDays) * TICKS_PER_DAY - 1,
        id: `${schedule.id}-instance`,
        scheduleId: schedule.id,
        startTick: world.tick,
        tileIds: affectedHazardTiles(world, schedule),
        type: schedule.type,
      };
      world.activeHazards.push(instance);
      sortById(world.activeHazards);
      promoteEvent(world, {
        actorIds: [],
        causeData: {
          durationDays: schedule.durationDays,
          triggerDay: schedule.triggerDay,
          weather: schedule.type,
        },
        causeIds: [schedule.id],
        continentId: schedule.continentId,
        effectData: {
          season: seasonNameFromDay(world.calendar.day),
        },
        groupingKey: `hazard:${schedule.id}`,
        location: { tileId: null },
        promotedBecause: ["active catastrophe", `${schedule.type} schedule`],
        retention: "history",
        severity: "critical",
        targetIds: [],
        type: "hazard-started",
      });
    }

    if (instance && shouldBeActive && !instance.active) {
      instance.active = true;
      instance.startTick = world.tick;
    }

    if (instance?.active && !shouldBeActive) {
      instance.active = false;
      instance.endTick = world.tick;
      promoteEvent(world, {
        actorIds: [],
        causeData: {
          endedOnDay: world.calendar.day,
          weather: instance.type,
        },
        causeIds: [schedule.id],
        continentId: schedule.continentId,
        effectData: { remainingActiveHazards: 0 },
        groupingKey: `hazard:${schedule.id}:end`,
        location: { tileId: null },
        promotedBecause: ["hazard duration ended"],
        retention: "history",
        severity: "notable",
        targetIds: [],
        type: "hazard-ended",
      });
    }
  }
}

function renewResources(world: WorldState) {
  const hazards = activeHazardTypes(world);
  if (hazards.includes("famine")) {
    for (const node of world.resourceNodes) {
      if (node.family !== "food") continue;
      node.quantity = round(Math.max(0, node.quantity - 0.08));
    }
  }
  if (world.tick % RESOURCE_RENEWAL_INTERVAL_TICKS !== 0) return;
  const season = world.weather.season;
  let seasonFactor = 1;
  if (season === "spring") seasonFactor = 1.25;
  if (season === "autumn") seasonFactor = 0.7;
  if (season === "winter") seasonFactor = 0.22;
  if (world.weather.precipitation === "rain") seasonFactor += 0.12;
  if (hazards.includes("drought")) seasonFactor = 0.08;
  if (hazards.includes("famine")) seasonFactor *= 0.2;
  for (const node of world.resourceNodes) {
    if (node.renewal <= 0) continue;
    node.quantity = round(
      Math.min(node.capacity, node.quantity + node.renewal * seasonFactor),
    );
  }
}

function nearestThreat(world: WorldState, creature: CreatureRecord) {
  return orderedLiving(world)
    .filter(
      (candidate) =>
        candidate.kind === "predator" &&
        candidate.continentId === creature.continentId &&
        candidate.id !== creature.id,
    )
    .map((predator) => ({
      predator,
      distance: distanceBetweenTileIds(world, creature.tileId, predator.tileId),
    }))
    .filter((entry) => entry.distance <= HUMANOID_FLEE_RADIUS)
    .sort(
      (left, right) =>
        left.distance - right.distance ||
        (left.predator.id < right.predator.id ? -1 : 1),
    )[0]?.predator;
}

function updateSafety(world: WorldState, creature: CreatureRecord) {
  let safety = 88;
  const threat = nearestThreat(world, creature);
  if (threat) {
    const distance = distanceBetweenTileIds(
      world,
      creature.tileId,
      threat.tileId,
    );
    safety = distance <= PREDATOR_ATTACK_RANGE ? 12 : 24;
  }
  if (creature.conditions.some((condition) => condition.kind === "injury")) {
    safety = Math.min(safety, 36);
  }
  if (
    world.activeHazards.some((hazard) =>
      hazardAffectsCreature(creature, hazard),
    )
  ) {
    safety = Math.min(safety, 48);
  }
  creature.needs.safety = round(safety);
}

function applyClimatePressure(world: WorldState, creature: CreatureRecord) {
  const resting = creature.action === "resting";
  const season = world.weather.season;
  if (season === "winter" && !resting) {
    creature.needs.warmth = round(Math.max(0, creature.needs.warmth - 0.05));
  }
  if (season === "summer") {
    creature.needs.water = round(Math.max(0, creature.needs.water - 0.02));
  }
  if (world.weather.precipitation === "rain") {
    creature.needs.rest = round(Math.max(0, creature.needs.rest - 0.02));
  }
  if (world.weather.precipitation === "snow" && !resting) {
    creature.needs.warmth = round(Math.max(0, creature.needs.warmth - 0.04));
  }
}

function applyHazardPressure(world: WorldState, creature: CreatureRecord) {
  const hazards = world.activeHazards.filter((hazard) =>
    hazardAffectsCreature(creature, hazard),
  );
  if (hazards.length === 0) {
    creature.conditions = creature.conditions.filter(
      (condition) => condition.kind !== "exposure",
    );
    return;
  }

  const resting = creature.action === "resting";
  const types = hazards.map((hazard) => hazard.type);
  if (types.includes("cold_snap")) {
    creature.needs.food = round(
      Math.max(0, creature.needs.food - (resting ? 0.01 : 0.04)),
    );
    creature.needs.warmth = round(
      Math.max(
        0,
        Math.min(NEED_MAX, creature.needs.warmth + (resting ? 0.32 : -0.25)),
      ),
    );
    if (!resting) {
      creature.health = round(Math.max(0, creature.health - 0.08));
    }
  }
  if (types.includes("storm") && !resting) {
    creature.needs.rest = round(Math.max(0, creature.needs.rest - 0.05));
    creature.needs.safety = round(Math.max(0, creature.needs.safety - 8));
    creature.health = round(Math.max(0, creature.health - 0.03));
  }
  if (types.includes("drought")) {
    creature.needs.water = round(Math.max(0, creature.needs.water - 0.06));
    creature.needs.food = round(Math.max(0, creature.needs.food - 0.03));
  }
  if (types.includes("famine")) {
    creature.needs.food = round(Math.max(0, creature.needs.food - 0.08));
  }

  if (
    !resting &&
    (types.includes("cold_snap") || types.includes("storm")) &&
    !creature.conditions.some((condition) => condition.kind === "exposure")
  ) {
    creature.conditions.push({
      cause: "hazard",
      id: `${creature.id}-exposure`,
      kind: "exposure",
      remainingTicks: TICKS_PER_DAY,
      sourceCreatureId: null,
    });
  }
}

function killCreature(
  world: WorldState,
  creature: CreatureRecord,
  cause: DeathCause,
  actorIds: string[],
) {
  if (!creature.alive) return;
  const eventId = `evt-${world.tick}-death-${creature.id}`;
  creature.alive = false;
  creature.action = "idle";
  creature.currentJob = "none";
  creature.currentTargetResourceNodeId = null;
  creature.currentTargetTileId = null;
  creature.health = 0;
  creature.death = { cause, eventId, tick: world.tick };
  promoteEvent(world, {
    actorIds,
    causeData: { cause },
    causeIds: actorIds,
    continentId: creature.continentId,
    effectData: { health: 0, removedFromDecisions: true },
    groupingKey: `death:${creature.id}`,
    id: eventId,
    location: { tileId: creature.tileId },
    promotedBecause:
      creature.kind === "humanoid" ? ["death of a person"] : ["wildlife death"],
    retention: "history",
    severity: creature.kind === "humanoid" ? "critical" : "notable",
    targetIds: [creature.id],
    type: "death",
  });
}

function injureCreature(
  world: WorldState,
  creature: CreatureRecord,
  cause: DeathCause,
  sourceCreatureId: string | null,
  damage: number,
) {
  creature.health = round(Math.max(0, creature.health - damage));
  const conditionId = `${creature.id}-injury-${world.tick}`;
  creature.conditions.push({
    cause,
    id: conditionId,
    kind: "injury",
    remainingTicks: INJURY_DURATION_TICKS,
    sourceCreatureId,
  });
  promoteEvent(world, {
    actorIds: sourceCreatureId ? [sourceCreatureId] : [],
    causeData: { cause, damage },
    causeIds: sourceCreatureId ? [sourceCreatureId] : [],
    continentId: creature.continentId,
    effectData: {
      health: creature.health,
      injuryDuration: INJURY_DURATION_TICKS,
    },
    groupingKey: `injury:${creature.id}:${world.tick}`,
    location: { tileId: creature.tileId },
    promotedBecause: ["injury with explicit cause"],
    retention: "history",
    severity: "ambient",
    targetIds: [creature.id],
    type: "injury",
  });
  if (creature.health <= 0) {
    killCreature(
      world,
      creature,
      cause,
      sourceCreatureId ? [sourceCreatureId] : [],
    );
  }
}

function resolvePredatorAttack(
  world: WorldState,
  predator: CreatureRecord,
  target: CreatureRecord,
  random: SeededRandom,
) {
  const roll = random.nextFloat();
  const predatorScore =
    predator.traits.strength + predator.traits.aggression * 0.2;
  const targetScore =
    target.traits.strength * 0.55 +
    (target.kind === "humanoid" ? 0.08 : 0) +
    (target.action === "fleeing" ? 0.12 : 0);
  let outcome: "escaped" | "injured" | "killed" = "escaped";
  if (predatorScore + roll * 0.18 >= targetScore + 0.42) {
    outcome = "killed";
    killCreature(world, target, "predator", [predator.id]);
    predator.needs.food = round(Math.min(NEED_MAX, predator.needs.food + 48));
  } else if (predatorScore + roll * 0.18 >= targetScore + 0.12) {
    outcome = "injured";
    injureCreature(world, target, "predator", predator.id, 28);
    predator.needs.food = round(Math.min(NEED_MAX, predator.needs.food + 18));
  } else {
    target.action = "fleeing";
    target.currentTargetTileId =
      world.startSites.find((site) => site.continentId === target.continentId)
        ?.tileId ?? target.tileId;
  }

  promoteEvent(world, {
    actorIds: [predator.id],
    causeData: {
      huntRadius: PREDATOR_HUNT_RADIUS,
      outcome,
      proximity: distanceBetweenTileIds(world, predator.tileId, target.tileId),
    },
    causeIds: [predator.speciesId],
    continentId: predator.continentId,
    effectData: {
      predatorFood: predator.needs.food,
      targetAlive: target.alive,
      targetHealth: target.health,
    },
    groupingKey: `predator-attack:${predator.id}:${target.id}:${world.tick}`,
    location: { tileId: target.tileId },
    promotedBecause: ["predator attack"],
    retention: "history",
    severity: outcome === "killed" ? "critical" : "notable",
    targetIds: [target.id],
    type: "predator-attack",
  });
}

function huntTarget(world: WorldState, predator: CreatureRecord) {
  const prey = orderedLiving(world)
    .filter(
      (candidate) =>
        candidate.kind === "herbivore" &&
        candidate.continentId === predator.continentId,
    )
    .map((candidate) => ({
      candidate,
      distance: distanceBetweenTileIds(
        world,
        predator.tileId,
        candidate.tileId,
      ),
    }))
    .filter((entry) => entry.distance <= PREDATOR_HUNT_RADIUS)
    .sort(
      (left, right) =>
        left.distance - right.distance ||
        (left.candidate.id < right.candidate.id ? -1 : 1),
    )[0]?.candidate;
  if (prey) return prey;

  const startSite = world.startSites.find(
    (site) => site.continentId === predator.continentId,
  );
  return orderedLiving(world)
    .filter((candidate) => {
      if (
        candidate.kind !== "humanoid" ||
        candidate.continentId !== predator.continentId
      ) {
        return false;
      }
      if (!startSite) return true;
      return (
        distanceBetweenTileIds(world, candidate.tileId, startSite.tileId) >
        startSite.protectedRadius
      );
    })
    .map((candidate) => ({
      candidate,
      distance: distanceBetweenTileIds(
        world,
        predator.tileId,
        candidate.tileId,
      ),
    }))
    .filter((entry) => entry.distance <= PREDATOR_HUNT_RADIUS)
    .sort(
      (left, right) =>
        left.distance - right.distance ||
        (left.candidate.id < right.candidate.id ? -1 : 1),
    )[0]?.candidate;
}

function chooseWildlifeDecision(
  world: WorldState,
  creature: CreatureRecord,
  random: SeededRandom,
) {
  if (creature.kind === "predator") {
    if (creature.needs.food < 52) {
      const target = huntTarget(world, creature);
      if (target) {
        const distance = distanceBetweenTileIds(
          world,
          creature.tileId,
          target.tileId,
        );
        setCreatureDecision(world, creature, {
          action: distance <= PREDATOR_ATTACK_RANGE ? "hunting" : "moving",
          currentJob: "hunter",
          reason:
            distance <= PREDATOR_ATTACK_RANGE
              ? "Attacking reachable prey because hunger is urgent."
              : "Hunting the nearest valid prey.",
          targetResourceNodeId: null,
          targetTileId: target.tileId,
        });
        if (distance <= PREDATOR_ATTACK_RANGE) {
          resolvePredatorAttack(world, creature, target, random);
        }
        return;
      }
    }

    const den = world.wildlifeSites.find(
      (site) =>
        site.continentId === creature.continentId &&
        site.kind === "predator-den",
    );
    setCreatureDecision(world, creature, {
      action: den && den.tileId !== creature.tileId ? "moving" : "idle",
      currentJob: "none",
      reason: "Waiting near the den while hunger is stable.",
      targetResourceNodeId: null,
      targetTileId: den?.tileId ?? creature.tileId,
    });
    return;
  }

  const threat = nearestThreat(world, creature);
  if (threat) {
    const origin = getTile(world, creature.tileId);
    const threatTile = getTile(world, threat.tileId);
    const range = world.wildlifeSites.find(
      (site) =>
        site.continentId === creature.continentId && site.kind === "prey-range",
    );
    const fallback = range?.tileId ?? creature.tileId;
    let fleeTileId = fallback;
    if (origin && threatTile) {
      const directions: [number, number][] = [
        [0, -1],
        [1, 0],
        [0, 1],
        [-1, 0],
      ];
      const safer = directions
        .map(([dx, dy]) =>
          getTile(world, (origin.y + dy) * WORLD_WIDTH + origin.x + dx),
        )
        .filter(
          (tile): tile is TileRecord =>
            tile !== undefined &&
            tile.continentId === creature.continentId &&
            isWalkableTile(tile),
        )
        .sort((left, right) => {
          const distanceDifference =
            distanceBetween(threatTile, right) -
            distanceBetween(threatTile, left);
          return distanceDifference || left.id - right.id;
        })[0];
      fleeTileId = safer?.id ?? fallback;
    }
    setCreatureDecision(world, creature, {
      action: "fleeing",
      currentJob: "none",
      reason: "Fleeing a predator inside the search radius.",
      targetResourceNodeId: null,
      targetTileId: fleeTileId,
    });
    return;
  }

  const range = world.wildlifeSites.find(
    (site) =>
      site.continentId === creature.continentId && site.kind === "prey-range",
  );
  const home = range
    ? getTile(world, range.tileId)
    : getTile(world, creature.tileId);
  const origin = getTile(world, creature.tileId);
  if (home && origin && distanceBetween(origin, home) > 4) {
    setCreatureDecision(world, creature, {
      action: "moving",
      currentJob: "none",
      reason: "Returning to the grazing range.",
      targetResourceNodeId: null,
      targetTileId: home.id,
    });
    return;
  }

  const grazeDirections: [number, number][] = [
    [0, -1],
    [1, 0],
    [0, 1],
    [-1, 0],
  ];
  const nearby = origin
    ? grazeDirections
        .map(([dx, dy]) =>
          getTile(world, (origin.y + dy) * WORLD_WIDTH + origin.x + dx),
        )
        .filter(
          (tile): tile is TileRecord =>
            tile !== undefined &&
            tile.continentId === creature.continentId &&
            isWalkableTile(tile),
        )
    : [];
  const wander = nearby[random.nextInt(0, Math.max(1, nearby.length))];
  setCreatureDecision(world, creature, {
    action: wander ? "grazing" : "idle",
    currentJob: "none",
    reason: "Grazing inside the local prey range.",
    targetResourceNodeId: null,
    targetTileId: wander?.id ?? creature.tileId,
  });
}

function performWildlifeAction(world: WorldState, creature: CreatureRecord) {
  if (
    creature.action === "moving" ||
    creature.action === "fleeing" ||
    creature.action === "grazing"
  ) {
    if (creature.movementCooldownTicks > 0) return;
    if (creature.currentTargetTileId !== null) {
      stepToward(world, creature, creature.currentTargetTileId);
    }
    creature.movementCooldownTicks =
      creature.kind === "predator" ? 1 : MOVEMENT_INTERVAL_TICKS;
    creature.needs.rest = round(Math.max(0, creature.needs.rest - 0.06));
  }
}

function applyHealthAndDeath(world: WorldState, creature: CreatureRecord) {
  if (!creature.alive) return;

  const remainingConditions: CreatureCondition[] = [];
  let recoveredFromInjury = false;
  for (const condition of creature.conditions) {
    condition.remainingTicks -= 1;
    if (condition.kind === "injury") {
      creature.health = round(Math.max(0, creature.health - 0.06));
    }
    if (condition.remainingTicks > 0) {
      remainingConditions.push(condition);
    } else if (condition.kind === "injury") {
      recoveredFromInjury = true;
    }
  }
  creature.conditions = remainingConditions;

  if (creature.needs.water <= 0) {
    creature.health = round(Math.max(0, creature.health - 0.2));
  } else if (creature.needs.food <= 0) {
    creature.health = round(Math.max(0, creature.health - 0.12));
  } else if (creature.needs.warmth <= 0) {
    creature.health = round(Math.max(0, creature.health - 0.15));
  } else if (
    world.tick % HEALTH_RECOVERY_INTERVAL_TICKS === 0 &&
    !creature.conditions.some((condition) => condition.kind === "injury") &&
    creature.health < HEALTH_MAX
  ) {
    creature.health = round(Math.min(HEALTH_MAX, creature.health + 0.8));
  }

  if (recoveredFromInjury) {
    promoteEvent(world, {
      actorIds: [creature.id],
      causeData: { recoveredBy: "injury duration ended" },
      causeIds: [creature.id],
      continentId: creature.continentId,
      effectData: { health: creature.health },
      groupingKey: `recovery:${creature.id}:${world.tick}`,
      location: { tileId: creature.tileId },
      promotedBecause: ["documented recovery"],
      retention: "history",
      severity: "ambient",
      targetIds: [creature.id],
      type: "recovery",
    });
  }

  if (creature.health <= 0) {
    const cause: DeathCause =
      creature.conditions[0]?.cause ??
      (creature.needs.water <= 0
        ? "dehydration"
        : creature.needs.food <= 0
          ? "starvation"
          : creature.needs.warmth <= 0
            ? "exposure"
            : "injury");
    killCreature(world, creature, cause, []);
  }
}

function chooseCreatureDecision(
  world: WorldState,
  creature: CreatureRecord,
  random: SeededRandom,
) {
  const threat = nearestThreat(world, creature);
  if (threat) {
    const startSite = world.startSites.find(
      (site) => site.continentId === creature.continentId,
    );
    setCreatureDecision(world, creature, {
      action: "fleeing",
      currentJob: "none",
      reason: "Fleeing a predator because safety is critical.",
      targetResourceNodeId: null,
      targetTileId: startSite?.tileId ?? creature.tileId,
    });
    return;
  }

  const waterUrgency = 68 - creature.needs.water;
  const foodUrgency = 60 - creature.needs.food;
  const urgentNeed =
    waterUrgency > 0 || foodUrgency > 0
      ? waterUrgency >= foodUrgency
        ? ("water" as const)
        : ("food" as const)
      : null;

  if (urgentNeed) {
    const target = findNearestResource(world, creature, urgentNeed);
    if (target) {
      const atTarget = creature.tileId === target.tileId;
      setCreatureDecision(world, creature, {
        action: atTarget ? "gathering" : "moving",
        currentJob: urgentNeed === "water" ? "water-gatherer" : "forager",
        reason: atTarget
          ? urgentNeed === "water"
            ? "Collecting water because thirst is rising."
            : "Gathering food because hunger is rising."
          : urgentNeed === "water"
            ? "Moving toward the nearest water source."
            : "Moving toward the nearest food source.",
        targetResourceNodeId: target.id,
        targetTileId: target.tileId,
      });
      return;
    }

    setCreatureDecision(world, creature, {
      action: "resting",
      currentJob: "none",
      reason: `No ${urgentNeed} source is available on this continent.`,
      targetResourceNodeId: null,
      targetTileId: null,
    });
    return;
  }

  if (creature.needs.rest < 44) {
    setCreatureDecision(world, creature, {
      action: "resting",
      currentJob: "none",
      reason: "Rest is low after recent work and travel.",
      targetResourceNodeId: null,
      targetTileId: null,
    });
    return;
  }

  if (world.tick % 30 === 0 && random.nextFloat() < 0.35) {
    const origin = getTile(world, creature.tileId);
    const directions: [number, number][] = [
      [0, -1],
      [1, 0],
      [0, 1],
      [-1, 0],
    ];
    const nearby = origin
      ? directions
          .map(([dx, dy]) =>
            getTile(world, (origin.y + dy) * WORLD_WIDTH + origin.x + dx),
          )
          .filter(
            (tile): tile is TileRecord =>
              tile !== undefined &&
              tile.continentId === creature.continentId &&
              isWalkableTile(tile),
          )
      : [];
    const target = nearby[random.nextInt(0, Math.max(1, nearby.length))];
    if (target) {
      setCreatureDecision(world, creature, {
        action: "moving",
        currentJob: "none",
        reason: "Exploring nearby ground while needs are stable.",
        targetResourceNodeId: null,
        targetTileId: target.id,
      });
      return;
    }
  }

  setCreatureDecision(world, creature, {
    action: "idle",
    currentJob: "none",
    reason: "Waiting while needs are stable.",
    targetResourceNodeId: null,
    targetTileId: null,
  });
}

function performCreatureAction(world: WorldState, creature: CreatureRecord) {
  if (creature.action === "resting") {
    creature.needs.rest = round(Math.min(NEED_MAX, creature.needs.rest + 0.18));
    creature.needs.warmth = round(
      Math.min(NEED_MAX, creature.needs.warmth + 0.12),
    );
    return;
  }

  if (creature.action === "moving" || creature.action === "fleeing") {
    if (creature.movementCooldownTicks > 0) return;
    if (creature.currentTargetTileId !== null) {
      stepToward(world, creature, creature.currentTargetTileId);
    }
    creature.movementCooldownTicks = MOVEMENT_INTERVAL_TICKS;
    creature.needs.rest = round(Math.max(0, creature.needs.rest - 0.08));
    return;
  }

  if (
    creature.action !== "gathering" ||
    creature.currentTargetResourceNodeId === null
  ) {
    return;
  }

  if (creature.gatherCooldownTicks > 0) return;
  const node = world.resourceNodes.find(
    (candidate) => candidate.id === creature.currentTargetResourceNodeId,
  );
  if (!node || node.quantity <= 0 || creature.tileId !== node.tileId) return;

  node.quantity = round(Math.max(0, node.quantity - RESOURCE_UNITS_PER_GATHER));
  if (node.family === "water")
    creature.inventory.water += RESOURCE_UNITS_PER_GATHER;
  if (node.family === "food")
    creature.inventory.food += RESOURCE_UNITS_PER_GATHER;
  creature.gatherCooldownTicks = GATHER_INTERVAL_TICKS;
  creature.needs.rest = round(Math.max(0, creature.needs.rest - 0.12));
}

function advanceCreatureNeeds(creature: CreatureRecord) {
  creature.needs.food = round(
    Math.max(0, creature.needs.food - NEED_DECAY_PER_TICK.food),
  );
  creature.needs.rest = round(
    Math.max(0, creature.needs.rest - NEED_DECAY_PER_TICK.rest),
  );
  creature.needs.water = round(
    Math.max(0, creature.needs.water - NEED_DECAY_PER_TICK.water),
  );
  creature.needs.warmth = round(Math.max(0, creature.needs.warmth - 0.03));
  creature.movementCooldownTicks = Math.max(
    0,
    creature.movementCooldownTicks - 1,
  );
  creature.gatherCooldownTicks = Math.max(0, creature.gatherCooldownTicks - 1);
}

function updateBuildings(world: WorldState) {
  for (const building of world.buildings) {
    const nearby = world.creatures.filter((creature) => {
      if (!creature.alive || creature.kind !== "humanoid") return false;
      if (creature.continentId !== building.continentId) return false;
      return (
        distanceBetweenTileIds(world, creature.tileId, building.tileId) <= 1
      );
    });
    building.workerCreatureIds = nearby
      .map((creature) => creature.id)
      .sort((left, right) => (left < right ? -1 : left > right ? 1 : 0));
    const resting = nearby.some((creature) => creature.action === "resting");
    const gathering = nearby.some(
      (creature) => creature.action === "gathering",
    );
    const carried = nearby.reduce(
      (total, creature) =>
        total + creature.inventory.food + creature.inventory.water,
      0,
    );
    building.storedUnits = building.kind === "storage" ? carried : 0;

    if (building.kind === "hearth") {
      building.making =
        nearby.length === 0
          ? "Cold ash, waiting for a firekeeper"
          : gathering
            ? "Cooked food and heat"
            : resting
              ? "Heat for rest and warmth"
              : BUILDING_CATALOG.hearth.idleMaking;
    } else if (building.kind === "shelter") {
      building.making =
        nearby.length === 0
          ? "Empty lean-to"
          : resting
            ? BUILDING_CATALOG.shelter.idleMaking
            : "Cover for the camp";
    } else if (building.kind === "storage") {
      building.making =
        carried > 0
          ? `Held stores: ${carried} food and water units`
          : BUILDING_CATALOG.storage.idleMaking;
    } else {
      building.making =
        nearby.length === 0
          ? BUILDING_CATALOG["work-surface"].idleMaking
          : "Simple tools and cordage";
    }
  }
}

function isReproductiveAdult(creature: CreatureRecord) {
  return (
    creature.alive &&
    creature.kind === "humanoid" &&
    creature.lifeStage === "adult" &&
    creature.ageYears >= REPRODUCTIVE_AGE_MIN &&
    creature.ageYears <= REPRODUCTIVE_AGE_MAX &&
    creature.health > 40 &&
    creature.needs.food > 25 &&
    creature.needs.water > 25
  );
}

function nextHumanoidId(
  world: WorldState,
  continentId: ContinentId,
  extra = 0,
) {
  let maximum = 0;
  for (const creature of world.creatures) {
    if (creature.kind !== "humanoid" || creature.continentId !== continentId) {
      continue;
    }
    const suffix = creature.id.split("-").at(-1);
    const value = suffix ? Number.parseInt(suffix, 10) : 0;
    if (Number.isInteger(value) && value > maximum) maximum = value;
  }
  return `${continentId}-humanoid-${String(maximum + extra + 1).padStart(2, "0")}`;
}

function inheritTraits(
  mother: CreatureRecord,
  father: CreatureRecord,
  random: SeededRandom,
) {
  const mix = (left: number, right: number) =>
    round(clamp((left + right) / 2 + (random.nextFloat() - 0.5) * 0.08));
  return {
    aggression: mix(mother.traits.aggression, father.traits.aggression),
    intelligence: mix(mother.traits.intelligence, father.traits.intelligence),
    riskTolerance: mix(
      mother.traits.riskTolerance,
      father.traits.riskTolerance,
    ),
    strength: mix(mother.traits.strength, father.traits.strength),
  } satisfies TraitVector;
}

function updateHouseholdsAndReproduction(world: WorldState) {
  if (world.tick === 0 || world.tick % TICKS_PER_DAY !== 0) return;
  const random = new SeededRandom(
    deriveSeed(world.seed, `reproduction:${world.calendar.day}`),
  );

  const births: CreatureRecord[] = [];
  const usedNames = new Set(world.creatures.map((creature) => creature.name));
  for (const mother of world.creatures) {
    if (!mother.alive || mother.kind !== "humanoid" || !mother.pregnancy) {
      continue;
    }
    mother.pregnancy.remainingDays -= 1;
    if (mother.pregnancy.remainingDays > 0) continue;
    const father =
      world.creatures.find(
        (creature) => creature.id === mother.pregnancy?.fatherId,
      ) ?? mother;
    const childSex: Sex = random.nextFloat() < 0.5 ? "female" : "male";
    const extra = births.filter(
      (child) => child.continentId === mother.continentId,
    ).length;
    const child: CreatureRecord = {
      ...initialCreatureStatus(random, "child"),
      ageYears: 0,
      alive: true,
      conditions: [],
      continentId: mother.continentId,
      death: null,
      health: HEALTH_MAX,
      householdId: mother.householdId,
      id: nextHumanoidId(world, mother.continentId, extra),
      kind: "humanoid",
      lifeStage: "child",
      name: createGeneratedCreatureName(random, usedNames),
      parentIds: [mother.id, father.id].sort(),
      pregnancy: null,
      profileId: mother.profileId,
      sex: childSex,
      speciesId: "humanoid",
      tileId: mother.tileId,
      traits: inheritTraits(mother, father, random),
    };
    if (mother.householdId) {
      const household = world.households.find(
        (entry) => entry.id === mother.householdId,
      );
      household?.memberIds.push(child.id);
      household?.memberIds.sort();
    }
    births.push(child);
    promoteEvent(world, {
      actorIds: [mother.id, father.id],
      causeData: { sex: childSex },
      causeIds: [mother.id, father.id],
      continentId: mother.continentId,
      effectData: { childId: child.id, name: child.name },
      groupingKey: `birth:${child.id}`,
      location: { tileId: mother.tileId },
      promotedBecause: ["a household child was born"],
      retention: "history",
      severity: "notable",
      targetIds: [child.id],
      type: "birth",
    });
    mother.pregnancy = null;
  }
  if (births.length > 0) {
    world.creatures.push(...births);
    sortById(world.creatures);
    sortById(world.households);
  }

  for (const continentId of CONTINENTS) {
    const adults = livingHumanoids(world)
      .filter(
        (creature) =>
          creature.continentId === continentId && isReproductiveAdult(creature),
      )
      .sort((left, right) => (left.id < right.id ? -1 : 1));
    const females = adults.filter(
      (creature) => creature.sex === "female" && creature.pregnancy === null,
    );
    const males = adults.filter((creature) => creature.sex === "male");
    const claimedMales = new Set<string>();

    for (const female of females) {
      const existingHousehold =
        female.householdId === null
          ? undefined
          : world.households.find((entry) => entry.id === female.householdId);
      let father = existingHousehold
        ? males.find(
            (male) =>
              existingHousehold.memberIds.includes(male.id) &&
              male.id !== female.id,
          )
        : undefined;

      if (female.householdId && !father) continue;

      if (!father) {
        father = males
          .filter(
            (male) => !claimedMales.has(male.id) && male.householdId === null,
          )
          .map((male) => ({
            distance: distanceBetweenTileIds(world, female.tileId, male.tileId),
            male,
          }))
          .sort(
            (left, right) =>
              left.distance - right.distance ||
              (left.male.id < right.male.id ? -1 : 1),
          )[0]?.male;
        if (!father) continue;
        claimedMales.add(father.id);
        const household: HouseholdRecord = {
          continentId,
          id: `${continentId}-household-${String(world.households.length + 1).padStart(2, "0")}`,
          memberIds: [father.id, female.id].sort(),
          tileId: female.tileId,
        };
        world.households.push(household);
        father.householdId = household.id;
        female.householdId = household.id;
        promoteEvent(world, {
          actorIds: [female.id, father.id],
          causeData: { bond: "camp-proximity" },
          causeIds: [],
          continentId,
          effectData: { householdId: household.id },
          groupingKey: `household:${household.id}`,
          location: { tileId: female.tileId },
          promotedBecause: ["two adults formed a household at camp"],
          retention: "history",
          severity: "ambient",
          targetIds: [household.id],
          type: "household-formed",
        });
      }

      if (random.nextFloat() > CONCEPTION_CHANCE) continue;
      female.pregnancy = {
        conceivedDay: world.calendar.day,
        fatherId: father.id,
        remainingDays: GESTATION_DAYS,
      };
    }
  }
}

export function advanceReproductionCalendar(world: WorldState, days: number) {
  if (!Number.isInteger(days) || days < 0) {
    throw new RangeError("Reproduction days must be a non-negative integer.");
  }
  for (let step = 1; step <= days; step += 1) {
    world.tick = step * TICKS_PER_DAY;
    world.calendar.tick = world.tick;
    world.calendar.day = step;
    world.calendar.seasonIndex =
      Math.floor(step / DAYS_PER_SEASON) % SEASON_NAMES.length;
    world.calendar.year = Math.floor(step / DAYS_PER_YEAR);
    updateHouseholdsAndReproduction(world);
  }
}

export function advanceWorldSimulation(
  world: WorldState,
  ticks: number,
  random: SeededRandom,
) {
  if (!Number.isInteger(ticks) || ticks < 0) {
    throw new RangeError("Simulation ticks must be a non-negative integer.");
  }

  for (let step = 0; step < ticks; step += 1) {
    advanceWorldTime(world, 1);
    updateHazards(world);
    updateWeather(world);
    renewResources(world);
    for (const creature of orderedLiving(world)) {
      advanceCreatureNeeds(creature);
      applyClimatePressure(world, creature);
      applyHazardPressure(world, creature);
      updateSafety(world, creature);
      if (creature.kind === "humanoid") {
        consumeHeldResources(creature);
        chooseCreatureDecision(world, creature, random);
        performCreatureAction(world, creature);
      } else {
        chooseWildlifeDecision(world, creature, random);
        performWildlifeAction(world, creature);
      }
      applyHealthAndDeath(world, creature);
      creature.mood = moodFor(creature);
    }
    updateBuildings(world);
    updateHouseholdsAndReproduction(world);
  }
}

export function serializeWorldState(world: WorldState) {
  const canonical = {
    ...world,
    continents: world.continents.map((continent) => ({
      ...continent,
      resourceNodeIds: [...continent.resourceNodeIds].sort(),
      tileIds: [...continent.tileIds].sort((left, right) => left - right),
    })),
    creatures: [...world.creatures].sort((left, right) =>
      left.id < right.id ? -1 : left.id > right.id ? 1 : 0,
    ),
    populationProfiles: [...world.populationProfiles].sort((left, right) =>
      left.id < right.id ? -1 : left.id > right.id ? 1 : 0,
    ),
    resourceNodes: [...world.resourceNodes].sort((left, right) =>
      left.id < right.id ? -1 : left.id > right.id ? 1 : 0,
    ),
    speciesDefinitions: [...world.speciesDefinitions].sort((left, right) =>
      left.id < right.id ? -1 : left.id > right.id ? 1 : 0,
    ),
    events: [...world.events].sort((left, right) =>
      left.id < right.id ? -1 : left.id > right.id ? 1 : 0,
    ),
    activeHazards: [...world.activeHazards].sort((left, right) =>
      left.id < right.id ? -1 : left.id > right.id ? 1 : 0,
    ),
    buildings: [...world.buildings].sort((left, right) =>
      left.id < right.id ? -1 : left.id > right.id ? 1 : 0,
    ),
    households: [...world.households].sort((left, right) =>
      left.id < right.id ? -1 : left.id > right.id ? 1 : 0,
    ),
    tiles: [...world.tiles].sort((left, right) => left.id - right.id),
  };
  return JSON.stringify(canonical);
}

export function worldChecksum(world: WorldState) {
  const serialized = serializeWorldState(world);
  let hash = 0x811c9dc5;
  for (let index = 0; index < serialized.length; index += 1) {
    hash ^= serialized.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, "0");
}

export function getTile(world: WorldState, tileId: number) {
  const direct = world.tiles[tileId];
  if (direct?.id === tileId) return direct;
  return world.tiles.find((tile) => tile.id === tileId);
}

export function isWalkableTile(tile: TileRecord) {
  return tile.terrain !== "deep-water" && tile.continentId !== null;
}
